import React, { useState, useEffect } from "react";
import { X, FileSpreadsheet } from "lucide-react";
import { useTemplateBuilder } from "../../../hooks/useTemplateBuilder";
import { PresetRail } from "./PresetRail";
import { ColumnCatalogPanel } from "./ColumnCatalogPanel";
import { AddCustomColumnPanel } from "./AddCustomColumnPanel";
import { SelectionSummaryBar } from "./SelectionSummaryBar";
import { SavePresetModal } from "./SavePresetModal";

export const TemplateBuilderModal = ({
  isOpen,
  onClose,
  currentTheme,
  theme = "light",
  isRTL = false,
  t = (k) => k,
  showAlert = (title, message, type) => window.alert(`${title}: ${message}`),
  modulePermissions = {},
  userRole = "",
  screenSize = "lg",
}) => {
  const [showAddCustom, setShowAddCustom] = useState(false);
  const [showSavePreset, setShowSavePreset] = useState(false);

  const builder = useTemplateBuilder({
    isOpen,
    t,
    showAlert,
    modulePermissions,
    userRole,
  });

  // Handle ESC key press to dismiss modal
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        if (showSavePreset) {
          // SavePresetModal handles its own Escape while saving is in flight.
        } else if (showAddCustom) {
          setShowAddCustom(false);
        } else {
          onClose();
        }
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, showAddCustom, showSavePreset, onClose]);

  if (!isOpen) return null;

  const isMobile = screenSize === "xs" || screenSize === "sm";

  return (
    <div
      className="modal-overlay"
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 99995,
        background: "rgba(0, 0, 0, 0.7)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: isMobile ? "10px" : "24px",
        direction: isRTL ? "rtl" : "ltr",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          maxWidth: "1100px",
          width: "100%",
          maxHeight: "90vh",
          height: isMobile ? "96vh" : "85vh",
          borderRadius: "16px",
          display: "flex",
          flexDirection: "column",
          background: theme === "dark" ? "#1e293b" : "#ffffff",
          color: currentTheme.colors.textPrimary,
          border: theme === "dark" ? "1px solid #334155" : "1px solid #e2e8f0",
          boxShadow: currentTheme.shadows.card || "0 20px 40px rgba(0,0,0,0.3)",
          overflow: "hidden",
          position: "relative",
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: isMobile ? "16px 20px" : "20px 24px",
            borderBottom: `1px solid ${currentTheme.colors.borderLight}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <div
              style={{
                width: "36px",
                height: "36px",
                borderRadius: "10px",
                background: `${currentTheme.colors.primary}18`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: currentTheme.colors.primary,
              }}
            >
              <FileSpreadsheet size={20} />
            </div>
            <div>
              <div style={{ fontSize: "1.05rem", fontWeight: 700 }}>
                {t("templateBuilder") || "Template Builder"}
              </div>
              <div
                style={{
                  fontSize: "0.82rem",
                  color: currentTheme.colors.textSecondary,
                  marginTop: "2px",
                }}
              >
                {t("templateBuilderSubtitle") || "Choose the columns to include, or export everything."}
              </div>
            </div>
          </div>

          <button
            type="button"
            onClick={onClose}
            style={{
              background: "transparent",
              border: "none",
              color: currentTheme.colors.textMuted,
              fontSize: "1.8rem",
              lineHeight: 1,
              cursor: "pointer",
              padding: "4px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            ×
          </button>
        </div>

        {/* Body (Two-Pane Layout) */}
        <div
          style={{
            flex: 1,
            display: "flex",
            flexDirection: isMobile ? "column" : isRTL ? "row-reverse" : "row",
            padding: isMobile ? "14px" : "20px 24px",
            gap: "20px",
            overflow: "hidden",
            position: "relative",
          }}
        >
          {/* Preset Rail (Left) */}
          <PresetRail
            presets={builder.presets}
            activePresetId={builder.activePresetId}
            isModified={builder.isModified}
            onSelectPreset={builder.applyPreset}
            onResetToFull={builder.resetToFullTemplate}
            onSaveNew={builder.saveAsNewPreset}
            onUpdatePreset={builder.updatePreset}
            onDeletePreset={builder.deletePreset}
            canShareCompany={builder.canShareCompany}
            currentTheme={currentTheme}
            theme={theme}
            isRTL={isRTL}
            t={t}
            screenSize={screenSize}
          />

          {/* Column Catalog Panel (Center) */}
          <ColumnCatalogPanel
            catalog={builder.catalog}
            filteredTypes={builder.filteredTypes}
            selectedTypes={builder.selectedTypes}
            customColumns={builder.customColumns}
            searchQuery={builder.searchQuery}
            setSearchQuery={builder.setSearchQuery}
            kindFilter={builder.kindFilter}
            setKindFilter={builder.setKindFilter}
            onToggleType={builder.toggleType}
            onToggleTypeVariant={builder.toggleTypeVariant}
            onToggleGlobalVariant={builder.toggleGlobalVariant}
            onToggleGroup={builder.toggleGroup}
            onToggleAllFiltered={builder.toggleAllFiltered}
            onOpenAddCustom={() => setShowAddCustom(true)}
            onRemoveCustomColumn={builder.removeCustomColumn}
            currentTheme={currentTheme}
            theme={theme}
            isRTL={isRTL}
            t={t}
            screenSize={screenSize}
          />

          {/* Slide-over: Add Custom Column Panel */}
          <AddCustomColumnPanel
            isOpen={showAddCustom}
            onClose={() => setShowAddCustom(false)}
            onAddColumn={builder.addCustomColumn}
            catalogTypes={builder.catalog.types}
            existingCustomColumns={builder.customColumns}
            currentTheme={currentTheme}
            theme={theme}
            isRTL={isRTL}
            t={t}
          />
        </div>

        {/* Save Preset dialog — themed replacement for window.prompt() */}
        <SavePresetModal
          isOpen={showSavePreset}
          onClose={() => setShowSavePreset(false)}
          onConfirm={async (payload) => {
            await builder.saveAsNewPreset(payload);
            setShowSavePreset(false);
          }}
          canShareCompany={builder.canShareCompany}
          currentTheme={currentTheme}
          theme={theme}
          isRTL={isRTL}
          t={t}
        />

        {/* Footer Summary Bar */}
        <SelectionSummaryBar
          totals={builder.totals}
          activePreset={builder.activePreset}
          isModified={builder.isModified}
          isExporting={builder.isExporting}
          onCancel={onClose}
          onExportFull={builder.exportFullTemplate}
          onExportFiltered={builder.exportFiltered}
          onSaveAsNew={() => setShowSavePreset(true)}
          onUpdateActivePreset={builder.updatePreset}
          currentTheme={currentTheme}
          theme={theme}
          isRTL={isRTL}
          t={t}
          screenSize={screenSize}
        />
      </div>
    </div>
  );
};
